import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { LayoutTemplate, Plus, Save, Trash2, X, Check, Star } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { ALL_PLATFORMS, PLATFORM_META } from "@/lib/platforms";

const STRUCTURE_MAX = 2000;
const TONE_MAX = 300;
const DESC_MAX = 200;
const NAME_MAX = 80;

const emptyDraft = () => ({
  id: null,
  name: "",
  description: "",
  target_format: "linkedin_post",
  structure: "",
  tone_override: "",
});

export default function Templates() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [mode, setMode] = useState("idle"); // idle | new | edit

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/templates");
      setItems(data.items || []);
    } catch {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const presets = useMemo(() => items.filter((t) => t.is_preset), [items]);
  const customs = useMemo(() => items.filter((t) => !t.is_preset), [items]);

  const startNew = () => {
    setDraft(emptyDraft());
    setMode("new");
  };

  const startEdit = (tpl) => {
    setDraft({
      id: tpl.id,
      name: tpl.name || "",
      description: tpl.description || "",
      target_format: tpl.target_format,
      structure: tpl.structure || "",
      tone_override: tpl.tone_override || "",
    });
    setMode("edit");
  };

  const cancelEdit = () => {
    setDraft(emptyDraft());
    setMode("idle");
  };

  const canSave =
    draft.name.trim().length > 0 &&
    draft.structure.trim().length >= 10 &&
    ALL_PLATFORMS.includes(draft.target_format) &&
    !saving;

  const handleSave = async () => {
    if (!canSave) {
      toast.error("Name and a structure (10+ chars) are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: draft.name.trim(),
        description: draft.description.trim() || null,
        target_format: draft.target_format,
        structure: draft.structure.trim(),
        tone_override: draft.tone_override.trim() || null,
      };
      if (mode === "edit" && draft.id) {
        await api.patch(`/templates/${draft.id}`, payload);
        toast.success(`"${payload.name}" updated`);
      } else {
        await api.post("/templates", payload);
        toast.success(`"${payload.name}" saved — pick it in the Studio to use it`);
      }
      cancelEdit();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"? This cannot be undone.`)) return;
    setDeletingId(tpl.id);
    try {
      await api.delete(`/templates/${tpl.id}`);
      toast.success("Deleted");
      if (draft.id === tpl.id) cancelEdit();
      await load();
    } catch {
      toast.error("Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  const isEditing = mode !== "idle";

  const renderCard = (tpl) => (
    <div
      key={tpl.id}
      data-testid={`template-card-${tpl.id}`}
      className="border border-neutral-200 bg-white p-6 transition-colors hover:border-black"
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {tpl.is_preset && (
              <span
                data-testid={`template-preset-badge-${tpl.id}`}
                className="inline-flex items-center gap-1 border border-neutral-300 bg-neutral-50 px-2 py-0.5 text-[10px] uppercase tracking-widest text-neutral-600"
              >
                <Star className="h-3 w-3" strokeWidth={1.5} />
                Preset
              </span>
            )}
            <span className="text-[10px] uppercase tracking-widest text-neutral-500">
              {PLATFORM_META[tpl.target_format]?.short || tpl.target_format}
            </span>
          </div>
          <h3
            data-testid={`template-name-${tpl.id}`}
            className="mt-2 font-serif text-2xl font-semibold tracking-tight"
          >
            {tpl.name}
          </h3>
          {tpl.description && (
            <p className="mt-1 line-clamp-2 text-sm text-neutral-600">{tpl.description}</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => handleDelete(tpl)}
          disabled={deletingId === tpl.id}
          data-testid={`template-delete-${tpl.id}`}
          className="p-1 text-neutral-500 hover:text-black"
          aria-label="Delete template"
        >
          <Trash2 className="h-4 w-4" strokeWidth={1.5} />
        </button>
      </div>

      {tpl.tone_override && (
        <p className="mb-3 line-clamp-1 text-xs italic text-neutral-500">
          Tone: &ldquo;{tpl.tone_override}&rdquo;
        </p>
      )}

      <pre className="mb-4 max-h-32 overflow-hidden whitespace-pre-wrap break-words font-sans text-[13px] leading-relaxed text-neutral-700">
        {tpl.structure}
      </pre>

      <div className="flex items-center gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => startEdit(tpl)}
          data-testid={`template-edit-${tpl.id}`}
          className="h-9 rounded-none border-neutral-300 text-xs"
        >
          Edit
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <main className="mx-auto max-w-5xl px-6 py-12 lg:px-10">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="mb-2 text-xs uppercase tracking-[0.25em] text-neutral-500">— Your structures</p>
            <h1 className="font-serif text-4xl font-bold tracking-tight sm:text-5xl">Templates</h1>
            <p className="mt-3 max-w-2xl text-sm text-neutral-600">
              Save reusable content structures — hook, points, CTA — and apply them in the Studio to
              enforce a specific shape for any generation.
            </p>
          </div>
          {!isEditing && (
            <Button
              onClick={startNew}
              data-testid="template-new-btn"
              className="h-12 rounded-[3px] bg-coral px-6 text-white transition-all duration-300 hover:bg-coral-hover hover:shadow-coral"
            >
              <Plus className="mr-2 h-4 w-4" strokeWidth={1.5} />
              New template
            </Button>
          )}
        </div>

        {!isEditing && (
          loading ? (
            <div className="flex justify-center py-24" data-testid="templates-loading">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-neutral-300 border-t-black" />
            </div>
          ) : items.length === 0 ? (
            <div className="border border-dashed border-neutral-300 bg-white p-16 text-center" data-testid="templates-empty">
              <LayoutTemplate className="mx-auto mb-4 h-8 w-8 text-neutral-400" strokeWidth={1.5} />
              <p className="font-serif text-2xl italic text-neutral-500">Design a structure once. Reuse it forever.</p>
              <p className="mt-2 max-w-md mx-auto text-sm text-neutral-500">
                Templates lock in a specific shape — like &ldquo;Bold Hook + 3 Points + CTA&rdquo; — so every
                generation follows it exactly. Great for building consistent, repeatable content.
              </p>
              <Button
                onClick={startNew}
                data-testid="templates-empty-cta"
                className="mt-6 h-12 rounded-[3px] bg-coral px-6 text-white transition-all duration-300 hover:bg-coral-hover hover:shadow-coral"
              >
                <Plus className="mr-2 h-4 w-4" strokeWidth={1.5} />
                Create your first template
              </Button>
            </div>
          ) : (
            <div className="space-y-10">
              {/* How-it-works hint */}
              <div
                data-testid="templates-how-it-works"
                className="flex items-start gap-4 border-l-4 border-black bg-white p-5"
              >
                <LayoutTemplate className="mt-0.5 h-5 w-5 flex-shrink-0" strokeWidth={1.5} />
                <div className="flex-1 text-sm text-neutral-700">
                  <p className="font-serif text-base font-semibold">How templates work</p>
                  <p className="mt-1 text-neutral-600">
                    Each template is tied to one format. Pick it from the Studio&apos;s Template
                    dropdown and every generation for that format will follow your structure —
                    section by section.
                  </p>
                </div>
              </div>

              {presets.length > 0 && (
                <section data-testid="templates-presets-section">
                  <p className="mb-4 text-xs uppercase tracking-[0.25em] text-neutral-500">
                    — Starter presets
                  </p>
                  <div className="grid gap-4 md:grid-cols-2">
                    {presets.map(renderCard)}
                  </div>
                </section>
              )}
              {customs.length > 0 && (
                <section data-testid="templates-customs-section">
                  <p className="mb-4 text-xs uppercase tracking-[0.25em] text-neutral-500">
                    — Your custom templates
                  </p>
                  <div className="grid gap-4 md:grid-cols-2">
                    {customs.map(renderCard)}
                  </div>
                </section>
              )}
            </div>
          )
        )}

        {/* Editor */}
        {isEditing && (
          <div className="space-y-6" data-testid="template-editor">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-[0.25em] text-neutral-500">
                — {mode === "edit" ? "Editing" : "New template"}
              </p>
              <button
                type="button"
                onClick={cancelEdit}
                data-testid="template-cancel-edit"
                className="text-xs text-neutral-500 underline underline-offset-4 hover:text-black"
              >
                Back to list
              </button>
            </div>

            {/* Name + description */}
            <div className="border border-neutral-200 bg-white p-6">
              <Label className="text-xs uppercase tracking-widest text-neutral-500">Template name</Label>
              <Input
                data-testid="template-name-input"
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value.slice(0, NAME_MAX) }))}
                placeholder='e.g. "Founder update", "Weekly recap"'
                className="mt-3 h-12 rounded-none border-neutral-300 focus-visible:ring-1 focus-visible:ring-black"
              />
              <p className="mt-2 text-xs text-neutral-400">{draft.name.length} / {NAME_MAX}</p>

              <Label className="mt-4 block text-xs uppercase tracking-widest text-neutral-500">
                Short description <span className="text-neutral-300">(optional)</span>
              </Label>
              <Input
                data-testid="template-description-input"
                value={draft.description}
                onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value.slice(0, DESC_MAX) }))}
                placeholder="Say what this template is best for"
                className="mt-2 h-12 rounded-none border-neutral-300 focus-visible:ring-1 focus-visible:ring-black"
              />
              <p className="mt-2 text-xs text-neutral-400">{draft.description.length} / {DESC_MAX}</p>
            </div>

            {/* Target format */}
            <div className="border border-neutral-200 bg-white p-6">
              <Label className="text-xs uppercase tracking-widest text-neutral-500">
                Target format
              </Label>
              <p className="mt-2 text-xs text-neutral-500">
                Templates are tied to one format. Pick the platform this structure is for.
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                {ALL_PLATFORMS.map((key) => {
                  const active = draft.target_format === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      data-testid={`template-format-chip-${key}`}
                      onClick={() => setDraft((d) => ({ ...d, target_format: key }))}
                      className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors ${
                        active
                          ? "border-coral bg-coral-subtle text-coral"
                          : "border-neutral-300 bg-white text-neutral-700 hover:border-black hover:text-black"
                      }`}
                    >
                      {active && <Check className="h-3.5 w-3.5" strokeWidth={1.5} />}
                      {PLATFORM_META[key].label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Structure */}
            <div className="border border-neutral-200 bg-white">
              <div className="border-b border-neutral-100 px-6 py-3">
                <Label className="text-xs uppercase tracking-widest text-neutral-500">Structure</Label>
                <p className="mt-1 text-xs text-neutral-500">
                  Describe the shape section-by-section. The AI will follow this exactly.
                </p>
              </div>
              <Textarea
                data-testid="template-structure-input"
                value={draft.structure}
                onChange={(e) => setDraft((d) => ({ ...d, structure: e.target.value.slice(0, STRUCTURE_MAX) }))}
                placeholder={"e.g.\n1. HOOK: one bold line, under 12 words.\n2. STORY: 3 short paragraphs about a real moment.\n3. LESSON: 1 sentence.\n4. CTA: a question."}
                className="min-h-[240px] resize-y rounded-none border-0 bg-transparent p-6 font-mono text-sm leading-relaxed placeholder:text-neutral-400 focus-visible:ring-0"
              />
              <div className="flex items-center justify-between border-t border-neutral-100 px-6 py-3 text-xs">
                <span className={draft.structure.trim().length >= 10 ? "text-emerald-600" : "text-neutral-400"}>
                  {draft.structure.trim().length >= 10 ? "✓ ready" : "add at least 10 characters"}
                </span>
                <span className="font-mono text-neutral-400">
                  {draft.structure.length.toLocaleString()} / {STRUCTURE_MAX.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Tone override */}
            <div className="border border-neutral-200 bg-white p-6">
              <Label className="text-xs uppercase tracking-widest text-neutral-500">
                Tone override <span className="text-neutral-300">(optional)</span>
              </Label>
              <p className="mt-2 text-xs text-neutral-500">
                If set, this replaces the brand-voice tone for generations using this template.
                Vocabulary and cadence from your voice samples still apply.
              </p>
              <Input
                data-testid="template-tone-input"
                value={draft.tone_override}
                onChange={(e) => setDraft((d) => ({ ...d, tone_override: e.target.value.slice(0, TONE_MAX) }))}
                placeholder="e.g. Sharp, opinionated, no hedging"
                className="mt-3 h-12 rounded-none border-neutral-300 focus-visible:ring-1 focus-visible:ring-black"
              />
              <p className="mt-2 text-xs text-neutral-400">{draft.tone_override.length} / {TONE_MAX}</p>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap items-center justify-between gap-4 border-t border-neutral-200 pt-8">
              <p className="text-xs text-neutral-500">
                {mode === "edit" ? "Changes apply to all future generations using this template." : "Pick this template in the Studio to enforce this shape."}
              </p>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={cancelEdit}
                  className="h-12 rounded-none border-neutral-300 text-neutral-700"
                  data-testid="template-cancel-btn"
                >
                  <X className="mr-2 h-4 w-4" strokeWidth={1.5} />
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={!canSave}
                  data-testid="template-save-btn"
                  className="h-12 rounded-[3px] bg-coral px-8 text-white transition-all duration-300 hover:bg-coral-hover hover:shadow-coral disabled:opacity-50"
                >
                  <Save className="mr-2 h-4 w-4" strokeWidth={1.5} />
                  {saving ? "Saving…" : mode === "edit" ? "Save changes" : "Save template"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
